import React from 'react';

import DataFrameColumnRelationshipMultiSelector from 'data-frames/column-relationship-selector/DataFrameColumnRelationshipMultiSelector';
import {DataFrameColumnRelationship} from 'data-frames/column-relationship-selector/DataFrameColumnRelationshipSelector';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineStepConfig} from 'pipelines/PipelineTypes';

import {getDataFrameSpecOrEmptySpec, useStepConfigEditorContext} from '../../StepConfigEditorContext';
import {CustomComponentEditorProps} from '../../StepConfigEditorTypes';
import {VariableHeaderOrNull} from '../StepConfigFieldsTemplate';

function getColumnRelationshipsFromConfig(
  config: PipelineStepConfig<PipelineStepType.UNION_DATASETS, 0, 'draft'>
): Array<DataFrameColumnRelationship> {
  return (config.primaryDataSourceColumns ?? []).map((leftColumn, index) => ({
    leftColumn,
    rightColumn:
      config.secondaryDataSourcesColumns &&
      config.secondaryDataSourcesColumns.length > 0 &&
      config.secondaryDataSourcesColumns[0].length > index
        ? config.secondaryDataSourcesColumns[0][index]
        : null,
  }));
}

function getConfigFromColumnRelationships(
  relationships: Array<DataFrameColumnRelationship>
): Pick<
  PipelineStepConfig<PipelineStepType.UNION_DATASETS, 0, 'draft'>,
  'primaryDataSourceColumns' | 'secondaryDataSourcesColumns'
> {
  return {
    primaryDataSourceColumns: relationships.map(({leftColumn}) => leftColumn),
    secondaryDataSourcesColumns: [relationships.map(({rightColumn}) => rightColumn)],
  };
}

/**
 * Custom editor for UNION_DATASETS steps. This component shares components with <CombineDataFramesView>.
 */
export default function UnionEditor({
  config,
  onChangeConfig,
  disabled,
  configShape,
}: CustomComponentEditorProps): JSX.Element {
  const {inputDataFrames} = useStepConfigEditorContext();
  const topDataFrame = getDataFrameSpecOrEmptySpec(inputDataFrames, 'Primary data frame');
  const bottomDataFrame = getDataFrameSpecOrEmptySpec(inputDataFrames, 'Secondary data frames');

  const relationships = getColumnRelationshipsFromConfig(
    config as PipelineStepConfig<PipelineStepType.UNION_DATASETS, 0, 'draft'>
  );

  return (
    <div className="u-small-spacing-top u-small-spacing-bottom">
      <VariableHeaderOrNull variableName="Map columns to union" isCompact={true} />
      <DataFrameColumnRelationshipMultiSelector
        leftDataFrame={topDataFrame}
        rightDataFrame={bottomDataFrame}
        relationships={relationships}
        onSelectRelationships={(newRelationships: Array<DataFrameColumnRelationship>): void => {
          onChangeConfig(config, [], {...config, ...getConfigFromColumnRelationships(newRelationships)});
        }}
        addItemLabel="Add a column pair"
        readOnly={disabled}
      />
    </div>
  );
}
